import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Download, Upload } from "lucide-react";
import { API_URL } from "../config";

export default function FileVersion() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const file = state?.file;
  const fileInputRef = useRef(null);
  const ownerEmail = localStorage.getItem("email") || "";

  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const formatBytes = (bytes) => {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const loadVersions = async () => {
    if (!file?.id) {
      setLoading(false);
      return;
    }
    try {
      setError("");
      const res = await fetch(
        `${API_URL}/api/secure-drive/files/${file.id}/versions?owner_email=${encodeURIComponent(ownerEmail)}`
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not load versions");
      setVersions(data);
    } catch (err) {
      console.error(err);
      setError(err.message || "Could not load versions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVersions();
  }, []);

  // backend checks permission, gets the locked file from IPFS and unlocks it
  const handleDownload = (v) => {
    window.open(
      `${API_URL}/api/secure-drive/download/${file.id}?version=${v.version}&owner_email=${encodeURIComponent(ownerEmail)}`,
      "_blank"
    );
  };

  const handleUploadVersion = async (e) => {
    const uploadedFile = e.target.files[0];
    e.target.value = "";
    if (!uploadedFile) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", uploadedFile);
      formData.append("owner_email", ownerEmail);
      formData.append("source", "upload");
      const res = await fetch(`${API_URL}/api/secure-drive/files/${file.id}/versions`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Upload failed");
      await loadVersions();
    } catch (err) {
      console.error(err);
      alert(err.message || "Could not upload the new version");
    } finally {
      setUploading(false);
    }
  };

  const current = versions.find((v) => v.is_current) || versions[0] || null;
  const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "-");
  const who = (email) => (email && email.toLowerCase() === ownerEmail.toLowerCase() ? "You" : email || "-");

  const card = {
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: "16px",
    padding: "24px",
  };
  const cols = "110px 110px 150px 240px 100px 200px 80px";

  return (
    <div
      style={{
        background: "#ffffff",
        minHeight: "100vh",
        padding: "20px 24px",
        fontFamily: "'Inter','Segoe UI',sans-serif",
        color: "#0F172A",
        position: "relative",
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: "18px" }}>
        <h1 style={{ fontSize: "17px", fontWeight: 700, color: "#0F172A", marginBottom: "8px" }}>
          File Versions
        </h1>
        <div style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "14px", color: "#334155" }}>
          <span
            onClick={() => navigate(-1)}
            style={{ cursor: "pointer", color: "#2563EB", fontWeight: "600" }}
          >
            ← My Drive
          </span>
          <span>{">"}</span>
          <span style={{ color: "#0F172A" }}>
            {current?.file_name || file?.file_name || "No File Selected"}
          </span>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "12px", marginBottom: "18px" }}>
        <div style={card}>
          <p style={{ color: "#64748B", marginBottom: "5px", fontSize: "14px" }}>Current Version</p>
          <h2 style={{ color: "#16A34A", margin: 0, fontSize: "20px" }}>
            {current ? `v${current.version}` : "v0"}
          </h2>
        </div>
        <div style={card}>
          <p style={{ color: "#64748B", marginBottom: "5px", fontSize: "14px" }}>Total Versions</p>
          <h2 style={{ margin: 0, color: "#0F172A", fontSize: "20px" }}>{versions.length}</h2>
        </div>
        <div style={card}>
          <p style={{ color: "#64748B", marginBottom: "5px", fontSize: "14px" }}>Latest Modified</p>
          <h3 style={{ margin: 0, color: "#0F172A", fontSize: "14px" }}>{fmtDate(current?.uploaded_at)}</h3>
        </div>
        <div style={card}>
          <p style={{ color: "#64748B", marginBottom: "5px", fontSize: "14px" }}>Current Size</p>
          <h2 style={{ margin: 0, color: "#0F172A", fontSize: "20px" }}>
            {current ? formatBytes(current.file_size) : "0 Bytes"}
          </h2>
        </div>
      </div>

      {/* Upload Button */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "14px" }}>
        <input type="file" ref={fileInputRef} onChange={handleUploadVersion} style={{ display: "none" }} />
        <button
          onClick={() => fileInputRef.current.click()}
          disabled={uploading || !file?.id}
          style={{
            background: "#2563EB",
            color: "#fff",
            border: "none",
            borderRadius: "10px",
            padding: "0 24px",
            height: "44px",
            cursor: uploading ? "default" : "pointer",
            opacity: uploading ? 0.6 : 1,
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontWeight: "600",
            fontSize: "14px",
            fontFamily: "'Inter','Segoe UI',sans-serif",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#1D4ED8")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#2563EB")}
        >
          <Upload size={14} />
          {uploading ? "Uploading..." : "Upload New Version"}
        </button>
      </div>

      {/* Table */}
      <div style={{ border: "1px solid #E2E8F0", borderRadius: "16px", overflowX: "auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: cols,
            background: "#F8FAFC",
            padding: "10px 12px",
            fontWeight: 700,
            fontSize: "14px",
            color: "#64748B",
          }}
        >
          <div>Version</div>
          <div>Type</div>
          <div>Uploaded By</div>
          <div>CID (IPFS)</div>
          <div>Size</div>
          <div>Modified On</div>
          <div>Actions</div>
        </div>

        {loading && (
          <div style={{ padding: "16px 12px", fontSize: "14px", color: "#94A3B8" }}>Loading versions...</div>
        )}
        {!loading && error && (
          <div style={{ padding: "16px 12px", fontSize: "14px", color: "#DC2626" }}>{error}</div>
        )}
        {!loading && !error && versions.length === 0 && (
          <div style={{ padding: "16px 12px", fontSize: "14px", color: "#94A3B8" }}>No versions found.</div>
        )}

        {versions.map((item) => (
          <div
            key={item.version}
            style={{
              display: "grid",
              gridTemplateColumns: cols,
              padding: "10px 12px",
              borderTop: "1px solid #E2E8F0",
              alignItems: "center",
              color: "#0F172A",
              fontSize: "14px",
            }}
          >
            <div style={{ color: item.is_current ? "#16A34A" : "#0F172A", fontWeight: "600" }}>
              v{item.version}
              {item.is_current ? " (Current)" : ""}
            </div>

            <div>
              <span
                style={{
                  padding: "2px 8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  borderRadius: "999px",
                  background: item.source === "signed" ? "#DCFCE7" : "#E0E7FF",
                  color: item.source === "signed" ? "#15803D" : "#3730A3",
                }}
              >
                {item.source === "signed" ? "Signed" : "Original"}
              </span>
            </div>

            <div>{who(item.uploaded_by)}</div>

            <div style={{ fontFamily: "monospace", fontSize: "11px", color: "#334155", wordBreak: "break-all" }}>
              {item.cid}
            </div>

            <div>{formatBytes(item.file_size)}</div>

            <div>{fmtDate(item.uploaded_at)}</div>

            <div>
              <Download
                size={15}
                style={{ cursor: "pointer", color: "#2563EB" }}
                onClick={() => handleDownload(item)}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div
        style={{
          marginTop: "16px",
          border: "1px solid #BFDBFE",
          background: "#EFF6FF",
          padding: "11px 14px",
          borderRadius: "9px",
          color: "#1E40AF",
          fontSize: "14px",
        }}
      >
        Each version is locked with its own key and stored on IPFS under its own CID. The CID identifies the locked
        copy; downloads are unlocked by the server for authorised users.
      </div>
    </div>
  );
}